import get from 'lodash.get';
import head from 'lodash.head';
import last from 'lodash.last';
import cloneDeep from 'lodash.clonedeep';
import isNil from 'lodash.isnil';
import isEqual from 'lodash.isequal';
import toPairs from 'lodash.topairs';
import isEmpty from 'lodash.isempty';
import { PARAM } from '../../constants/shared/ApiConstants';
import { getTrcInterpretation } from '../../constants/shared/TrcInterpretationMapper';
import { getCommonInterpretation } from '../../constants/shared/CommonInterpretationMapper';
import { KEY, INTERP, DISTRICT_GROUPS, MUNI_GROUPS } from '../../constants/shared/CommonConstants';
import { MEASURE, VOCABO_MEASURE } from '../../constants/shared/MeasuresConstants';
import WEB_ENTRY from '../../constants/shared/WebEntryConstants';
import { getSortedResultsByDate } from './sortUtils';
import { getStudentResultByMeasureYearGradePeriod } from './filterUtils';
import { isAdditionalMeasure } from './additionalMeasureUtils';
import { isSecure } from './commonUtils';

const { GENERATING_RESULT_BY_ASSESSMENT_IMPORTER } = PARAM;
const { INTERPRETATION, OVERALL_SCORE, CLIENT_MOD_DATE, OVERALL_INTERP, PERCENTILE } = KEY;
const { BLANK } = INTERP;
const { VOCABO, TRC, MAZE, OVERALL, CLPL } = MEASURE;

export const compareResultByStudentMeasureYearGradePeriod = (
    result,
    studentId,
    measure,
    year,
    grade,
    period,
) =>
    result.student_uid === studentId &&
    (measure ? result.measure === measure : true) &&
    result.school_year === year &&
    result.grade_level === grade &&
    result.period === period;

export const getStudentResultsByMeasureYearGradePeriod = (
    results,
    studentId,
    measure,
    year,
    grade,
    period,
) =>
    (results || []).filter(result =>
        compareResultByStudentMeasureYearGradePeriod(
            result,
            studentId,
            measure,
            year,
            grade,
            period,
        ),
    );

export const getTRCResult = (resultGroup, result) => ({
    ...resultGroup,
    formGrade: resultGroup.grade,
    isPresumed: result.is_presumed,
    // In original here should be result.result_id but we changed it to result.internal_result_id
    // because of inconsistency between responses from old TRC API and new one.
    // This fix is temporary and it should be reverted after
    // https://amplify-education.atlassian.net/browse/NOM-16110 story will be done
    resultId: result.internal_result_id,
});

export const getTRCStatus = (resultGroup, result) => ({
    ...resultGroup,
    formGrade: resultGroup.grade,
    score: {
        rawValue: resultGroup.score,
        bookType: get(result, 'book.book_type'),
        performanceLevel: get(result, 'performance_level'),
    },
    // In original here should be result.result_id but we changed it to result.internal_result_id
    // because of inconsistency between responses from old TRC API and new one.
    // This fix is temporary and it should be reverted after
    // https://amplify-education.atlassian.net/browse/NOM-16110 story will be done
    resultId: result.internal_result_id,
    date: result.client_mod_date,
});

export const isImportedResult = dataSource =>
    dataSource === GENERATING_RESULT_BY_ASSESSMENT_IMPORTER;

export const hasUnofficialResults = (resultGroups, studentId, year, period) => {
    const studentGroups = resultGroups.filter(
        group =>
            group.student_uid === studentId &&
            group.school_year === year &&
            group.period === period &&
            group.is_complete,
    );
    return studentGroups.some(group => get(group, 'officialness_rank') > 1);
};

/**
 * Find TRC result that can be considered as a final within result group.
 * For complete results the finalResultId is id of the final result.
 * In other cases this is the latest.
 * @param {Array} trcResults
 * @param {string} finalResultId
 */
export const findFinalTRCResult = (trcResults, finalResultId) =>
    isNil(finalResultId)
        ? last(trcResults)
        : trcResults.find(
              ({ internal_result_id: internalResultId }) => internalResultId === finalResultId,
          );

export const getParsedTrcStatus = result => ({
    schoolYear: result.schoolYear,
    period: result.period,
    grade: result.grade,
    formGrade: result.grade,
    measure: TRC,
    score: {
        rawValue: get(result, 'score'),
        bookType: get(result, 'bookType'),
        performanceLevel: get(result, 'performanceLevel'),
    },
    interp: getTrcInterpretation(get(result, 'interpName')),
    isComplete: result.complete,
    isUnofficial: result.hasUnofficialScore,
    resultId: result.resultSid,
    date: result.stringDateCompleted,
    isSecure: result.isSecure,
});

export const getVocaboStatus = (status, result) => {
    const isComplete = !isNil(status.complete_date);
    return {
        measure: VOCABO,
        schoolYear: status.school_year,
        period: status.period,
        grade: status.grade_level,
        resultId: isComplete ? get(result, 'result_id') : null,
        score: get(status, OVERALL_SCORE),
        interp: getCommonInterpretation(get(status, INTERPRETATION)),
        isComplete,
        date: status.complete_date,
        isSecure: isSecure(result),
    };
};

const getVocaboSubResults = results =>
    results.map(result => ({
        measure: result.measure,
        formUid: result.form_id,
        score: result.measure_result.measure_score_list[0].score,
        responses: result.measure_result.responses,
    }));

export const getVocaboResult = (status, results) => {
    const tqvmResult = results.find(result => result.measure === VOCABO_MEASURE.TQVM);

    return {
        measure: VOCABO,
        schoolYear: status.school_year,
        period: status.period,
        grade: status.grade_level,
        formGrade: status.grade_level,
        studentId: status.student_uid,
        resultId: get(tqvmResult, 'result_id'),
        date: status.complete_date,
        subResults: getVocaboSubResults(results),
        measureScores: {
            [VOCABO]: {
                score: get(status, OVERALL_SCORE),
                interp: getCommonInterpretation(get(status, INTERPRETATION)),
                maxScore: results
                    .map(result => result.measure_result.measure_score_list[0].total_items)
                    .reduce((score1, score2) => score1 + score2),
            },
        },
        isSecure: isSecure(tqvmResult),
    };
};

export const getR3DResult = (result, measure, studentId) => ({
    measure,
    period: result.period,
    studentId,
    grade: result.grade,
    formGrade: result.grade,
    interp: getTrcInterpretation(get(result, 'interpName')),
    score: result.score,
    isPresumed: result.presumedResult,
    schoolYear: result.schoolYear,
    resultGroupId: result.resultGroupUid,
    resultId: result.resultSid,
    isComplete: result.complete,
    isUnofficial: result.hasUnofficialScore,
    isSecure: isSecure(result),
});

export const removeExistingResults = (data, statusStateKey, measures) => {
    const existingStudentsData = cloneDeep(data);
    Object.keys(existingStudentsData).forEach(studentId => {
        const student = existingStudentsData[studentId];
        const results = student.results.filter(result => !measures.includes(result.measure));
        const statusStateKeyData = student[statusStateKey].filter(
            status => !measures.includes(status.measure),
        );

        existingStudentsData[studentId] = {
            ...student,
            results: [...results],
            [statusStateKey]: [...statusStateKeyData],
        };
    });
    return existingStudentsData;
};

/**
 * Returns date of composite completion based on the last of given results
 */
export const getCompositeDate = results => {
    const resultsSortedByDate = getSortedResultsByDate(results, CLIENT_MOD_DATE);
    const filteredResultsByCompositeContribution = resultsSortedByDate.filter(
        ({ measure, grade_level: grade }) => !isAdditionalMeasure(measure, grade),
    );
    return head(filteredResultsByCompositeContribution)[CLIENT_MOD_DATE];
};

export const hasSubmeasures = (item, property, count) =>
    item[property] && Object.keys(item[property]).length >= count;

/**
 * Split results for measures which have submeasures
 */
export const splitResultsToSubmeasures = (item, property) =>
    Object.keys(item[property]).map(submeasure => {
        const clonedResult = { ...item };
        const updatedData = {
            measure: submeasure,
            [property]: { [submeasure]: item[property][submeasure] },
        };
        return { ...clonedResult, ...updatedData };
    });

export const getScore = result =>
    result.measure === MAZE || result.measure === CLPL
        ? get(result, 'raw_score')
        : get(result.measure_scores[result.measure], 'score');

export const getStatusScore = (item, isOverallScore) =>
    isOverallScore ? get(item, OVERALL_SCORE) : getScore(item);

export const getGatedMeasureObject = (gatedMeasureScore, measure) => {
    if (gatedMeasureScore && gatedMeasureScore[measure]) {
        return {
            gatingType: gatedMeasureScore[measure].gating_type,
            imputedScore: gatedMeasureScore[measure].imputed_score,
            interpretation: gatedMeasureScore[measure].interpretation,
            gatingDate: gatedMeasureScore[measure].server_mod_date,
        };
    }
    return {};
};

export const getInterpretationFromStudentResult = (result, isOverallScore) => {
    if (result.measure === MAZE || result.measure === CLPL) return get(result, INTERPRETATION);
    if (isOverallScore) return get(result, OVERALL_INTERP);
    return get(result, `measure_scores[${result.measure}][${INTERPRETATION}]`);
};

export const formatObservations = observations =>
    !isNil(observations)
        ? observations.map(({ observation_uid: id, parameters }) => ({ id, parameters }))
        : [];

export const buildStatus = (item, isOverallScore, isGatedMeasure) => {
    const {
        gatingType = null,
        imputedScore = null,
        interpretation = null,
        gatingDate = null,
    } = getGatedMeasureObject(item.gated_measure_scores, item.measure);
    const interp = isGatedMeasure
        ? interpretation
        : getInterpretationFromStudentResult(item, isOverallScore);
    const date = isGatedMeasure ? gatingDate : item[CLIENT_MOD_DATE];
    const isBasicComprehension = isEqual(item.measure, MAZE) || isEqual(item.measure, CLPL);
    const formGrade =
        isImportedResult(item.data_source) || isGatedMeasure ? item.grade_level : item.form_grade;
    const percentilePath = isBasicComprehension
        ? PERCENTILE
        : `measure_scores.${item.measure}.${PERCENTILE}`;
    const percentile = isOverallScore ? item.percentile : get(item, percentilePath);

    return {
        measure: isOverallScore ? OVERALL : item.measure,
        schoolYear: item.school_year,
        period: item.period,
        dataSource: item.data_source,
        grade: item.grade_level,
        formGrade,
        resultId: isBasicComprehension && item.data_source === WEB_ENTRY ? null : item.result_id,
        score: isGatedMeasure ? imputedScore : getStatusScore(item, isOverallScore),
        date: isBasicComprehension ? item.create_date : date,
        interp: getCommonInterpretation(interp),
        isComplete: isBasicComprehension ? get(item, 'is_complete', null) : true,
        gatingType,
        ...(isBasicComprehension && { isWebEntry: item.data_source === WEB_ENTRY }),
        formNumber: item.form_number,
        percentile,
        observations: formatObservations(item.observations),
        dyslexiaRiskLevel: item.dyslexia_risk_level,
        isSecure: isSecure(item),
        attemptNumber: item.attempt_number,
    };
};

export const getStatuses = (item, isOverallScore, prepareStatus) => {
    const hasScoresSubmeasures = hasSubmeasures(item, 'measure_scores', 2);
    const hasGatedMeasures = hasSubmeasures(item, 'gated_measure_scores', 1);
    if (hasScoresSubmeasures) {
        return splitResultsToSubmeasures(item, 'measure_scores').map(status =>
            prepareStatus(status, isOverallScore, false),
        );
    }
    if (hasGatedMeasures) {
        const overallStatus = prepareStatus(item, isOverallScore, false);
        const gatedStatuses = splitResultsToSubmeasures(item, 'gated_measure_scores').map(status =>
            prepareStatus(status, false, true),
        );
        return [overallStatus, ...gatedStatuses];
    }
    return Array.of(prepareStatus(item, isOverallScore, false));
};

export const getOLStatus = (result, hasUnofficialScore) => ({
    measure: result.measure,
    schoolYear: result.school_year,
    period: result.period,
    grade: result.grade,
    resultId: result.result_id,
    score: result.score,
    interp: getCommonInterpretation(result.interpretation) || BLANK,
    isComplete: result.is_complete,
    isUnofficial: hasUnofficialScore,
    date: result.client_mod_date,
    isSecure: isSecure(result),
});

export const updateStudentObject = (students, studentId, resultData, stateProperties) => {
    if (!students[studentId]) {
        const studentObject = {};
        stateProperties.forEach(property => {
            studentObject[property] = [];
        });
        return {
            ...students,
            [studentId]: {
                ...studentObject,
                ...resultData,
            },
        };
    }
    const existingStudent = students[studentId];
    const updatedResultData = {};
    Object.keys(resultData).forEach(resultDataKey => {
        updatedResultData[resultDataKey] = [
            ...get(existingStudent, resultDataKey, []),
            ...resultData[resultDataKey],
        ];
    });
    return {
        ...students,
        [studentId]: {
            ...existingStudent,
            ...updatedResultData,
        },
    };
};

export const mergeStudentData = (data, studentResultsMap, properties) => {
    const clonedStudentsData = cloneDeep(data);
    toPairs(studentResultsMap).forEach(([studentId, studentResults]) => {
        if (!isEmpty(clonedStudentsData) && clonedStudentsData[studentId]) {
            const student = clonedStudentsData[studentId];
            properties.forEach(property => {
                student[property] = get(student, property, []).concat(studentResults[property]);
            });
        } else {
            Object.assign(clonedStudentsData, { [studentId]: studentResults });
        }
    });
    return clonedStudentsData;
};

export const setResultsAndStatuses = (stateProperties, students, studentId, statuses, results) =>
    updateStudentObject(students, studentId, { results, statuses }, stateProperties);

export const setAdditionalStatuses = (stateProperties, students, studentId, statuses) => {
    return updateStudentObject(
        students,
        studentId,
        { additionalStatuses: statuses },
        stateProperties,
    );
};

export const deleteBasicComprehensionResultFromStudentObject = (
    data,
    studentId,
    resultId,
    basicComprehensionMeasure,
) => {
    const studentData = cloneDeep(data[studentId]);
    const studentResults = get(studentData, 'results', []);
    const studentStatuses = get(studentData, 'statuses', []);
    const result = studentResults.find(studentResult => studentResult.resultId === resultId);
    const status = getStudentResultByMeasureYearGradePeriod(
        studentStatuses,
        basicComprehensionMeasure,
        result.schoolYear,
        result.grade,
        result.period,
    );
    studentResults.splice(studentResults.indexOf(result), 1);
    studentStatuses.splice(studentStatuses.indexOf(status), 1);
    return {
        ...data,
        [studentId]: {
            ...studentData,
            results: studentResults,
            statuses: studentStatuses,
        },
    };
};

export const getDemoClassFlag = (isDemoClass, classId) =>
    isDemoClass ? { class_id: classId } : {};

export const isMuniOrDistrictGroupUser = schoolId =>
    [DISTRICT_GROUPS, MUNI_GROUPS].includes(schoolId);

export const getStudentResults = (status, results) =>
    getStudentResultsByMeasureYearGradePeriod(
        results,
        status.student_uid,
        null,
        status.school_year,
        status.grade_level,
        status.period,
    ).map(result => ({
        ...result,
        // Remap date field for consistency
        [CLIENT_MOD_DATE]: get(result, CLIENT_MOD_DATE, result.create_date),
    }));

export const getCompositeStatus = (status, studentResults) => ({
    ...status,
    ...(!isNil(status[OVERALL_SCORE]) && {
        [CLIENT_MOD_DATE]: getCompositeDate(studentResults),
    }),
});
